<script lang="ts" setup>
import { TuiTooltipDirective as vTooltip } from '@clickbar/tailwindui-vue'

import {
  HOVERED_ELEMENT_KEY,
  HOVERED_ELEMENTS_KEY,
  ID_TO_PAGE_ELEMENT_KEY,
  pageElementSpecs,
  removeElement,
  ROOT_CONTAINER_KEY,
  SELECTED_ELEMENT_KEY,
  type PageElement,
  type PageElementDataOfType,
  type PageElementOfType,
  type PageElementType,
  type PageGroupSection,
} from '../page-builder'
import PageBuilderDragLine from '../PageBuilderDragLine.vue'

import type { Directive } from 'vue'

const props = defineProps<{
  element: PageElementOfType<'group'>
}>()

const dragHovering = ref(false)
const container = ref<HTMLDivElement>()
const idToElement = inject(ID_TO_PAGE_ELEMENT_KEY)!
const hoveredElements = inject(HOVERED_ELEMENTS_KEY)!
const hoveredElement = inject(HOVERED_ELEMENT_KEY)!
const selectedElement = inject(SELECTED_ELEMENT_KEY)!
const rootContainer = inject(ROOT_CONTAINER_KEY)!

/** Fired when hovering over the body of the empty group. */
function onDragOver(dragEvent: DragEvent) {
  if (props.element.data.sections.length > 0) {
    return
  }

  const dataTransfer = dragEvent.dataTransfer!
  if (!dataTransfer.types.some((x) => x.startsWith('application/x-page-element-'))) {
    return
  }

  dragEvent.preventDefault()

  if (dataTransfer.types.includes('application/x-page-element-type')) {
    dataTransfer.dropEffect = 'copy'
  } else if (dataTransfer.types.includes('application/x-page-element-id')) {
    dataTransfer.dropEffect = 'move'
  }

  dragHovering.value = true
}

function onDragLeave() {
  dragHovering.value = false
}

/** Fired when dropping something into the body of an empty group. */
function onDrop(dragEvent: DragEvent) {
  if (props.element.data.sections.length > 0) {
    return
  }

  dragHovering.value = false
  dragEvent.preventDefault()

  const dataTransfer = dragEvent.dataTransfer!

  if (dataTransfer.types.includes('application/x-page-element-type')) {
    const type = dataTransfer.getData('application/x-page-element-type') as PageElementType
    const section = createNewSectionWithElementOfType(type)

    // eslint-disable-next-line vue/no-mutating-props
    props.element.data.sections.push(section)
  } else if (dataTransfer.types.includes('application/x-page-element-id')) {
    const id = dataTransfer.getData('application/x-page-element-id')
    const element = idToElement.get(id)
    if (!element) {
      return
    }

    removeElement(element, idToElement, hoveredElements)

    const section: PageGroupSection = {
      parent: props.element,
      id: crypto.randomUUID(),
      blocks: [{ element, size: 6, alignment: 'stretch' }],
    }
    element.parent = section
    idToElement.set(element.id, element)

    // eslint-disable-next-line vue/no-mutating-props
    props.element.data.sections.push(section)
  }
}

function createNewSectionWithElementOfType(type: PageElementType) {
  const section: PageGroupSection = {
    parent: props.element,
    id: crypto.randomUUID(),
    blocks: [],
  }

  const element: PageElement = {
    parent: section,
    id: crypto.randomUUID(),
    type,
    data: pageElementSpecs[type].createNewData() as any,
  }
  idToElement.set(element.id, element)

  section.blocks.push({ element, size: 6, alignment: 'stretch' })

  return section
}

function dropAtSectionIndex(event: DragEvent, index: number) {
  const dataTransfer = event.dataTransfer!

  if (dataTransfer.types.includes('application/x-page-element-type')) {
    const type = dataTransfer.getData('application/x-page-element-type') as PageElementType
    const section = createNewSectionWithElementOfType(type)

    // eslint-disable-next-line vue/no-mutating-props
    props.element.data.sections.splice(index, 0, section)
  } else if (dataTransfer.types.includes('application/x-page-element-id')) {
    const id = dataTransfer.getData('application/x-page-element-id')
    const element = idToElement.get(id)
    if (!element) {
      return
    }

    const indexInGroup = props.element.data.sections.findIndex(
      (x) => x.blocks.length === 1 && x.blocks[0].element === element,
    )
    if (indexInGroup !== -1 && indexInGroup < index) {
      // If we know the remove will shift the elements, we need to shift the index
      index--
    }

    removeElement(element, idToElement, hoveredElements)

    const section: PageGroupSection = {
      parent: props.element,
      id: crypto.randomUUID(),
      blocks: [{ element, size: 6, alignment: 'stretch' }],
    }
    element.parent = section
    idToElement.set(element.id, element)

    // eslint-disable-next-line vue/no-mutating-props
    props.element.data.sections.splice(index, 0, section)
  }
}

function insertInSectionAtIndex(
  event: DragEvent,
  section: PageElementDataOfType<'group'>['sections'][number],
  index: number,
) {
  const dataTransfer = event.dataTransfer!
  if (dataTransfer.types.includes('application/x-page-element-type')) {
    if (section.blocks.length === 3) {
      return
    }

    const type = dataTransfer.getData('application/x-page-element-type') as PageElementType
    const element: PageElement = {
      parent: section,
      id: crypto.randomUUID(),
      type,
      data: pageElementSpecs[type].createNewData() as any,
    }
    idToElement.set(element.id, element)

    section.blocks.splice(index, 0, { element, size: -1, alignment: 'stretch' })
  } else if (dataTransfer.types.includes('application/x-page-element-id')) {
    const id = dataTransfer.getData('application/x-page-element-id')
    const element = idToElement.get(id)
    if (!element) {
      return
    }

    const indexInSection = section.blocks.findIndex((x) => x.element === element)
    if (indexInSection !== -1) {
      if (indexInSection < index) {
        // If we know the remove will shift the elements, we need to shift the index
        index--
      }

      const block = section.blocks[indexInSection]

      // Directly swap within the section without deleting the element. This also retains the relative block sizes.
      section.blocks.splice(indexInSection, 1)
      section.blocks.splice(index, 0, block)

      return
    } else if (section.blocks.length === 3) {
      return
    }

    removeElement(element, idToElement, hoveredElements)

    element.parent = section
    idToElement.set(element.id, element)

    section.blocks.splice(index, 0, { element, size: -1, alignment: 'stretch' })
  }

  for (const block of section.blocks) {
    block.size = 6 / section.blocks.length // Guaranteed to be an integer
  }
}

function insertInSectionAtIndexIsLegal(
  event: DragEvent,
  section: PageElementDataOfType<'group'>['sections'][number],
): boolean {
  if (section.blocks.length < 3) {
    // Always legal
    return true
  }

  const dataTransfer = event.dataTransfer!
  if (!dataTransfer.types.some((x) => x.startsWith('application/x-page-element-id='))) {
    return false
  }

  const id = dataTransfer.types
    .find((x) => x.startsWith('application/x-page-element-id='))!
    .slice('application/x-page-element-id='.length)
  const element = idToElement.get(id)
  if (!element) {
    return false
  }

  // Only legal if the operation is a rearrangement
  return section.blocks.some((x) => x.element === element)
}

function resizeElementInSection(
  section: PageElementDataOfType<'group'>['sections'][number],
  index: number,
  newEndPos: number,
) {
  const currentEndPos = section.blocks
    .slice(0, index + 1)
    .reduce((sum, block) => sum + block.size, 0)
  if (currentEndPos === newEndPos) {
    return
  }

  const element = section.blocks[index]
  const newSize = element.size + (newEndPos - currentEndPos)
  if (newSize <= 0) {
    return
  }

  const nextElement = section.blocks[index + 1]
  const newSizeOfNextElement = nextElement.size - (newSize - element.size)
  if (newSizeOfNextElement <= 0) {
    return
  }

  element.size = newSize
  nextElement.size = newSizeOfNextElement
}

function onElementDragStart(dragEvent: DragEvent, element: PageElement) {
  if (element === selectedElement.value) {
    selectedElement.value = null
  }

  const dataTransfer = dragEvent.dataTransfer!
  dataTransfer.effectAllowed = 'move'
  dataTransfer.setData('application/x-page-element-id', element.id)

  // We also need a version where the data is encoded into the key directly. This is because values cannot be read out
  // in any events before the drop event.
  dataTransfer.setData('application/x-page-element-id=' + element.id, '')

  if (element.type === 'group') {
    // We need to ensure that a group is never dragged into itself to avoid any cyclic graphs. By disabling pointer
    // events, we prevent anything in the group from being a valid drop target.
    setTimeout(() => {
      ;(dragEvent.target as HTMLElement).style.pointerEvents = 'none'
    })
  }
}

function onElementDragEnd(dragEvent: DragEvent, element: PageElement) {
  if (element.type === 'group') {
    ;(dragEvent.target as HTMLElement).style.pointerEvents = ''
  }
}

const vPossiblyChangeAnchor: Directive = {
  mounted(element: HTMLDivElement) {
    const rootContainerRect = rootContainer.value!.getBoundingClientRect()
    const rightThreshold = rootContainerRect.right - 16

    const elementRect = element.getBoundingClientRect()

    // See if the element extends beyond the right side of the root container. If it does, we'll need to anchor it
    // differently.
    if (elementRect.right > rightThreshold) {
      element.style.left = 'initial'
      element.style.right = '0'
    }
  },
}

function onElementContainerPointerDown(event: PointerEvent) {
  let htmlElement = event.target as HTMLElement | null
  while (htmlElement && !htmlElement.classList.contains('_element-container')) {
    htmlElement = htmlElement.parentElement
  }

  if (!htmlElement) {
    return
  }

  // We make the container draggable only when we want it to be. This is better than cancelling the drag event when we
  // don't want it, because on Firefox, text selection breaks down on draggable elements even if the drag is canceled.
  // This behavior is relevant for the editors. It seems to link back to the following long-standing bug:
  // https://bugzilla.mozilla.org/show_bug.cgi?id=800050
  htmlElement.draggable = true

  window.addEventListener(
    'pointerup',
    () => {
      htmlElement.draggable = false
    },
    { once: true },
  )
}
</script>

<template>
  <div
    class="w-full border-2 rounded-md border-dashed"
    :class="{
      '!border-zinc-300 bg-zinc-200': dragHovering,
      'p-2 bg-white shadow border-transparent': element.data.renderAsCard,
      'bg-zinc-100': !element.data.renderAsCard,
    }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <TuiEmptyState
      v-if="element.data.sections.length === 0"
      title="Elemente hierhin ziehen"
      class="!py-4 pointer-events-none"
    >
      <template #icon="iconProps">
        <ITablerDragDrop v-bind="iconProps" />
      </template>
    </TuiEmptyState>

    <div v-else ref="container">
      <PageBuilderDragLine
        orientation="horizontal"
        :container
        @drop="dropAtSectionIndex($event, 0)"
      />

      <template v-for="(section, sectionIndex) in element.data.sections" :key="section.id">
        <div class="flex">
          <PageBuilderDragLine
            class="shrink-0"
            orientation="vertical"
            :container
            :drag-is-legal="(event) => insertInSectionAtIndexIsLegal(event, section)"
            @drop="insertInSectionAtIndex($event, section, 0)"
          />

          <template v-for="(block, blockIndex) in section.blocks" :key="block.element.id">
            <div
              class="_element-container min-w-0 rounded-lg relative p-0.5 select-none"
              :class="{
                'bg-primary-100':
                  hoveredElement === block.element || selectedElement === block.element,
                'bg-primary-200 z-20': selectedElement === block.element,
                'rounded-t-none':
                  selectedElement === block.element && pageElementSpecs[block.element.type].editor,
              }"
              :style="{
                flex: `${block.size} ${block.size} 0px`,
                alignSelf: block.alignment,
              }"
              @dragstart.stop="onElementDragStart($event, block.element)"
              @dragend.stop="onElementDragEnd($event, block.element)"
              @pointerenter="hoveredElements.add(block.element)"
              @pointerleave.stop="hoveredElements.delete(block.element)"
              @click.stop="selectedElement = block.element"
              @pointerdown.stop="onElementContainerPointerDown($event)"
            >
              <component
                :is="pageElementSpecs[block.element.type].renderer()"
                class="h-full"
                :element="block.element"
              />

              <div
                v-if="
                  section.blocks.length > 1 &&
                  (hoveredElement === block.element || selectedElement === block.element)
                "
                class="absolute left-1 -bottom-3 bg-primary-100 rounded flex"
                @click.stop
              >
                <button
                  v-tooltip="'Oben ausrichten'"
                  type="button"
                  class="rounded hover:bg-primary-200"
                  @click="block.alignment = 'start'"
                >
                  <IMaterialSymbolsVerticalAlignTopRounded
                    class="size-4 opacity-50 p-0.5 box-content hover:opacity-100"
                    :class="{
                      '!opacity-100': block.alignment === 'start',
                    }"
                  />
                </button>

                <button
                  v-tooltip="'Mittig ausrichten'"
                  type="button"
                  class="rounded hover:bg-primary-200"
                  @click="block.alignment = 'center'"
                >
                  <IMaterialSymbolsVerticalAlignCenterRounded
                    class="size-4 opacity-50 p-0.5 box-content hover:opacity-100"
                    :class="{
                      '!opacity-100': block.alignment === 'center',
                    }"
                  />
                </button>

                <button
                  v-tooltip="'Unten ausrichten'"
                  type="button"
                  class="rounded hover:bg-primary-200"
                  @click="block.alignment = 'end'"
                >
                  <IMaterialSymbolsVerticalAlignBottomRounded
                    class="size-4 opacity-50 p-0.5 box-content hover:opacity-100"
                    :class="{
                      '!opacity-100': block.alignment === 'end',
                    }"
                  />
                </button>

                <button
                  v-tooltip="'Höhe ausfüllen'"
                  type="button"
                  class="rounded hover:bg-primary-200"
                  @click="block.alignment = 'stretch'"
                >
                  <IMdiArrowUpDown
                    class="size-4 opacity-50 p-0.5 box-content hover:opacity-100"
                    :class="{
                      '!opacity-100': block.alignment === 'stretch',
                    }"
                  />
                </button>
              </div>

              <button
                v-if="hoveredElement === block.element || selectedElement === block.element"
                v-tooltip="'Element löschen'"
                type="button"
                class="absolute -bottom-3 right-1 bg-primary-100 rounded hover:bg-primary-200"
                @click.stop="removeElement(block.element, idToElement, hoveredElements)"
              >
                <IHeroiconsSolidTrash
                  class="size-4 opacity-50 p-0.5 box-content hover:opacity-100"
                />
              </button>

              <template
                v-if="
                  selectedElement === block.element && pageElementSpecs[block.element.type].editor
                "
              >
                <div class="absolute bg-primary-200 bottom-full inset-x-0 h-2" />

                <div
                  v-possibly-change-anchor
                  class="absolute overflow-hidden bg-primary-200 p-2 w-full min-w-96 bottom-full left-0 select-text rounded-lg"
                  @pointerdown.stop
                >
                  <component
                    :is="pageElementSpecs[block.element.type].editor!()"
                    v-model:data="block.element.data"
                  />
                </div>
              </template>
            </div>

            <PageBuilderDragLine
              class="shrink-0"
              orientation="vertical"
              :draggable="blockIndex < section.blocks.length - 1"
              :container
              :drag-is-legal="(event) => insertInSectionAtIndexIsLegal(event, section)"
              @drop="insertInSectionAtIndex($event, section, blockIndex + 1)"
              @drag="resizeElementInSection(section, blockIndex, $event)"
            />
          </template>
        </div>

        <PageBuilderDragLine
          orientation="horizontal"
          :container
          @drop="dropAtSectionIndex($event, sectionIndex + 1)"
        />
      </template>
    </div>
  </div>
</template>
